import { useEffect, useState } from 'react';
import { Routes, Route } from 'react-router-dom';
import { Header } from './components/Header';
import { Footer } from './components/Footer';
import { LoadingSpinner } from './components/LoadingSpinner';
import { ScrollToTop } from './components/ScrollToTop';
import DynamicPage from './components/DynamicPage';
import Blog from './pages/Blog';
import BlogPost from './pages/BlogPost';
// CPR Training Direct pages
import CPRHome from './pages/cpr/CPRHome';
import CoursesOverview from './pages/cpr/CoursesOverview';
import CourseDetail from './pages/cpr/CourseDetail';
import Book from './pages/cpr/Book';
import Testimonials from './pages/cpr/Testimonials';
import Resources from './pages/cpr/Resources';
import Contact from './pages/cpr/Contact';
import Privacy from './pages/cpr/legal/Privacy';
import Terms from './pages/cpr/legal/Terms';
import Accessibility from './pages/cpr/legal/Accessibility';
import { blogSlug } from './config';

/**
 * Main Application Component
 * 
 * Provides the core application structure with routing, header, and footer.
 * Implements a hybrid approach supporting both WordPress CMS content and local React components.
 * 
 * @returns {JSX.Element} The main app component with routing configuration
 */
export const App = () => {
  // Application initialization state
  const [isLoading, setIsLoading] = useState(true);

  /**
   * Initialize the application on mount
   * Currently minimal, but can be extended for theme loading, auth checks, etc.
   */
  useEffect(() => {
    // Initialize app - placeholder for future initialization logic
    // Could include: theme loading, authentication checks, global state setup
    setIsLoading(false);
  }, []);

  // Show loading spinner during app initialization
  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="min-h-screen flex flex-col" data-testid="app-container">
      {/* Scroll to top on route change */}
      <ScrollToTop />

      {/* Site header with navigation */}
      <Header />

      {/* Main content area with routing */}
      <main className="flex-grow" data-testid="main-content">
        <Routes>
          {/* CPR Training Direct - Home */}
          <Route path="/" element={<CPRHome />} />

          {/* CPR Training Direct - Courses (specific before dynamic) */}
          <Route path="/courses" element={<CoursesOverview />} />
          <Route path="/courses/:slug" element={<CourseDetail />} />

          {/* CPR Training Direct - Core Pages */}
          <Route path="/book" element={<Book />} />
          <Route path="/testimonials" element={<Testimonials />} />
          <Route path="/resources" element={<Resources />} />
          <Route path="/contact" element={<Contact />} />

          {/* CPR Training Direct - Legal Pages */}
          <Route path="/legal/privacy" element={<Privacy />} />
          <Route path="/legal/terms" element={<Terms />} />
          <Route path="/legal/accessibility" element={<Accessibility />} />

          {/* Blog routes */}
          <Route path="/blog" element={<Blog />} />
          <Route path={`/${blogSlug}/:slug`} element={<BlogPost />} />

          {/* Template demo routes - COMMENTED OUT for CPR Training Direct
          <Route path="/ecommerce-demo" element={<EcommerceDemo />} />
          <Route path="/smPosts" element={<SocialMediaPosts />} />
          <Route path="/elearning" element={<ELearningPageSimple />} />
          */}

          {/* Dynamic page route - handles WordPress pages and local components */}
          <Route path="/:slug" element={<DynamicPage />} />

          {/* 404 catch-all - MUST be last */}
          <Route path="*" element={<DynamicPage />} />
        </Routes>
      </main>
      
      {/* Site footer */}
      <Footer />
    </div>
  );
}; 